import { Controller } from "@hotwired/stimulus"
import { track as trackEvent } from "../lib/mixpanel"
import { issueTagChip } from "../lib/issue_tag"

// Phase 2.10 Tier 3 — Apryse WebViewer compare mode.
//
// Boots WebViewer with `WebViewer.startCompare(leftUrl, rightUrl)`
// — the same Apryse instance that ships single-PDF read-only
// viewing on DevPdf / DrawingsSpec / Specification / Punchlist
// surfaces (RD #6 revised: NO PDF.js / HexaPDF rastering / external
// SaaS). The server (`DocumentLockingController#compare`) resolves
// both presigned S3 URLs and writes the audit row + emits the
// `document_version.compared` webhook BEFORE the client renders, so
// a browser crash / network drop after the URL handoff still leaves
// a complete audit trail.
//
// Listens for the `pdf-compare:open` window event dispatched by the
// audit drawer's "Compare with vN" button. Mounts in the same modal
// shell the existing single-PDF viewer uses — toggling between
// modes happens via the `mode` data attribute on the modal root.
export default class extends Controller {
  static targets = ["viewerContainer", "title", "loading", "diffStrip", "bluebeamHint"]
  static values = {
    licenseKey: String,
    compareUrl: String,
    targetType: String,
    targetId: Number,
    retryLabel: { type: String, default: "Try again" }
  }

  connect() {
    this._instance = null
    this._onOpen = this._onOpen.bind(this)
    window.addEventListener("pdf-compare:open", this._onOpen)
  }

  disconnect() {
    window.removeEventListener("pdf-compare:open", this._onOpen)
    if (this._instance) {
      try {
        this._instance.UI.dispose()
      } catch (e) { /* dispose can be a no-op when never booted */ }
      this._instance = null
    }
  }

  // ── Public entry — opens the compare drawer ────────────────────
  // event.detail = { targetType, targetId, leftVersionId, rightVersionId }
  async _onOpen(event) {
    const detail = event.detail || {}
    if (
      this.hasTargetTypeValue &&
      detail.targetType &&
      detail.targetType !== this.targetTypeValue
    ) {
      return // not addressed at this controller instance
    }
    if (
      this.hasTargetIdValue &&
      detail.targetId &&
      Number(detail.targetId) !== Number(this.targetIdValue)
    ) {
      return
    }

    this._lastDetail = detail
    this._revealRoot()
    this._showLoading()
    try {
      const json = await this._fetchCompareUrls(detail)
      if (!json.ok) {
        this._showError(json.message || "Could not load comparison")
        return
      }

      if (this.hasBluebeamHintTarget) {
        const bluebeam = json.right?.source === "bluebeam_studio"
        this.bluebeamHintTarget.classList.toggle("hidden", !bluebeam)
      }

      if (json.mode === "apryse_compare") {
        await this._renderApryseCompare(json.left.url, json.right.url, json)
      } else if (json.mode === "text_diff") {
        this._renderTextDiff(json)
      } else {
        this._renderMetadataOnly(json)
      }

      trackEvent("Document Versions Compared", {
        target_type: detail.targetType || this.targetTypeValue,
        target_id: detail.targetId || this.targetIdValue,
        mode: json.mode,
        left_source: json.left?.source,
        right_source: json.right?.source
      })
    } catch (e) {
      console.error("[pdf-compare] compare failed:", e)
      this._showError(`Network error: ${e?.message || e}`)
    }
  }

  async _fetchCompareUrls(detail) {
    const body = new FormData()
    body.append("target_type", detail.targetType || this.targetTypeValue)
    body.append("target_id", detail.targetId || this.targetIdValue)
    body.append("left_version_id", detail.leftVersionId)
    body.append("right_version_id", detail.rightVersionId)

    const resp = await fetch(this.compareUrlValue, {
      method: "POST",
      headers: { "X-CSRF-Token": this._csrfToken(), "Accept": "application/json" },
      body
    })
    return resp.json().catch(() => ({ ok: false, message: "Bad response" }))
  }

  // Apryse's WebViewer global is defined by /webviewer/webviewer.min.js.
  // The single-PDF viewer loads it on demand; the compare modal can open
  // without that ever having run, so make sure the script is present first.
  async _ensureWebViewerLoaded() {
    if (typeof window.WebViewer === "function") return
    if (!this._webViewerScriptPromise) {
      this._webViewerScriptPromise = new Promise((resolve, reject) => {
        const existing = document.querySelector('script[src="/webviewer/webviewer.min.js"]')
        if (existing) {
          existing.addEventListener("load", resolve)
          existing.addEventListener("error", () => reject(new Error("Failed to load WebViewer script")))
          if (typeof window.WebViewer === "function") resolve()
          return
        }
        const script = document.createElement("script")
        script.src = "/webviewer/webviewer.min.js"
        script.onload = resolve
        script.onerror = () => reject(new Error("Failed to load WebViewer script"))
        document.head.appendChild(script)
      })
    }
    return this._webViewerScriptPromise
  }

  // ── Apryse compare boot ─────────────────────────────────────────
  //
  // The native compare API (`WebViewer.startCompare`) loads two PDFs
  // into a side-by-side document panel with change highlighting +
  // sync-scroll out of the box. We disable annotation editing so the
  // diff stays purely a comparison surface (annotations on the
  // working copy stay in the regular single-PDF viewer).
  async _renderApryseCompare(leftUrl, rightUrl, json) {
    if (!this._instance) {
      await this._ensureWebViewerLoaded()
      // Clear a Try again message (or a text diff) left in the container.
      this.viewerContainerTarget.innerHTML = ""
      this._instance = await window.WebViewer({
        path: "/webviewer",
        licenseKey: this.licenseKeyValue,
        fullAPI: true,
        disableWebWorkers: true
      }, this.viewerContainerTarget)

      // Keep Apryse's native compare chrome (top header, compare-panel
      // toggle, overlay legend) so this matches the built-in "Compare
      // Pages" experience. Only suppress the markup popups, since this is
      // a comparison surface, not a markup canvas.
      this._instance.UI.disableElements([
        "annotationPopup",
        "contextMenuPopup"
      ])

      // Force the light theme. The compare "Change List" panel renders its
      // Old/New diff snippets with light-mode colors (pale red/green boxes
      // with dark text), which become unreadable when the viewer inherits a
      // dark theme. Pinning light keeps the diff legible.
      try { this._instance.UI.setTheme("light") } catch (_) { /* older SDKs */ }
    }

    const { UI, Core } = this._instance

    // WebViewer 11 — enter the dual-pane "MultiViewer" mode and then
    // auto-activate the "Compare Pages" overlay (semantic diff). This is
    // the same thing the View ▸ Page Layout ▸ Compare Pages menu does, but
    // triggered programmatically so the user lands straight on the diff.
    //
    // Left pane (documentViewer 1) = OLDER version, right pane = newer, so
    // changes read before → after left-to-right.
    await new Promise((resolve, reject) => {
      let settled = false
      const onReady = async () => {
        UI.removeEventListener(UI.Events.MULTI_VIEWER_READY, onReady)
        try {
          const [dv1, dv2] = Core.getDocumentViewers()

          // Load both panes, resolving on documentLoaded and rejecting on
          // loaderror so a bad document fails fast instead of hanging.
          const loaded = (dv) => new Promise((res, rej) => {
            dv.addEventListener("documentLoaded", () => res(), { once: true })
            dv.addEventListener("loaderror", (e) => rej(new Error(e?.message || "Document failed to load")), { once: true })
          })
          const p1 = loaded(dv1)
          const p2 = loaded(dv2)

          // Order by version so the layout is deterministic regardless of the
          // order the caller passed them in (bulk-select and "compare to
          // previous" hand them in different orders): OLDER on the left, NEWER
          // on the right, so changes read before → after left-to-right.
          const { older, newer } = this._orderByVersion(json.left, json.right)

          // Fetch each PDF as a blob first (the single-PDF viewer does the
          // same) — handing Apryse the bytes sidesteps S3 presigned-URL CORS
          // / range-request issues that make a direct URL load hang.
          const [newerBlob, olderBlob] = await Promise.all([
            fetch(newer.url).then(r => { if (!r.ok) throw new Error(`Newer HTTP ${r.status}`); return r.blob() }),
            fetch(older.url).then(r => { if (!r.ok) throw new Error(`Older HTTP ${r.status}`); return r.blob() })
          ])
          // Load each pane through ITS OWN document viewer. UI.loadDocument
          // with { documentViewerKey: 2 } does NOT route to the second pane
          // (it silently re-loads pane 1), so the right pane never fires
          // documentLoaded and the whole thing times out.
          // Older on the LEFT, newer on the right. Name each pane after its
          // version, e.g. "V1 — C-8 (older)" / "V2 — C-8 (newer)".
          dv1.loadDocument(olderBlob, { filename: `${this._paneName(older, "older")}.pdf`, extension: "pdf" })
          dv2.loadDocument(newerBlob, { filename: `${this._paneName(newer, "newer")}.pdf`, extension: "pdf" })
          await Promise.all([p1, p2])

          // Fire the overlay comparison ("Compare Pages"): differences are
          // highlighted on both panes (deletions on the left/before,
          // additions on the right/after). Stop any prior diff first — on a
          // reused viewer a stale comparison can otherwise block the new one.
          //
          // Semantic diff is part of Apryse's LICENSED Document Comparison
          // package, which our license doesn't include — a licensing
          // exception here used to abort the whole flow ("Network error").
          // Degrade to plain synced side-by-side panes instead; the list's
          // Overlay compare covers the "show me what changed" need.
          let diffActive = false
          if (typeof dv1.stopSemanticDiff === "function") {
            try { dv1.stopSemanticDiff() } catch (_) { /* none active */ }
          }
          if (typeof dv1.startSemanticDiff === "function") {
            try {
              await dv1.startSemanticDiff(dv2)
              diffActive = true
            } catch (e) {
              console.warn("[pdf-compare] semantic diff unavailable — plain side-by-side:", e?.message || e)
            }
          }

          // Open the native comparison results panel (the change list) so
          // the view matches the built-in "Compare Pages" experience — only
          // when the diff actually ran (it feeds the panel).
          if (diffActive) {
            try { UI.openElements(["comparePanel"]) } catch (_) { /* panel not present in all builds */ }
          }

          // Lock the two panes' scrolling together by default ("Start Sync").
          // enableMultiViewerSync(viewerKey) is the on/off switch (1 = use the
          // left pane as the scroll primary); setMultiViewerSyncScrollingMode
          // picks the behavior. Done LAST — startSemanticDiff resets the sync
          // state, so enabling it earlier wouldn't stick.
          if (typeof UI.setMultiViewerSyncScrollingMode === "function") {
            try { UI.setMultiViewerSyncScrollingMode("SYNC") } catch (_) { /* unsupported */ }
          }
          if (typeof UI.enableMultiViewerSync === "function") {
            try { UI.enableMultiViewerSync(1) } catch (_) { /* unsupported */ }
          } else if (typeof UI.setMultiViewerSyncScrolling === "function") {
            UI.setMultiViewerSyncScrolling(true)
          }

          settled = true
          resolve()
        } catch (e) {
          reject(e)
        }
      }
      // If the viewer is already in multi-viewer mode (reused instance from a
      // previous compare), enterMultiViewerMode() will NOT re-fire
      // MULTI_VIEWER_READY — so go straight to loading. Otherwise wait for the
      // event the way a fresh boot does.
      if (Core.getDocumentViewers().length > 1) {
        onReady()
      } else {
        UI.addEventListener(UI.Events.MULTI_VIEWER_READY, onReady)
        // enterMultiViewerMode triggers MULTI_VIEWER_READY once both panes mount.
        UI.enterMultiViewerMode()
      }
      // Safety: if the event never fires, don't hang the loader forever.
      setTimeout(() => { if (!settled) reject(new Error("Compare view timed out")) }, 30000)
    })

    if (this.hasTitleTarget) {
      const { older, newer } = this._orderByVersion(json.left, json.right)
      this.titleTarget.innerHTML = `${this._paneTitleHtml(older, "older")} ↔ ${this._paneTitleHtml(newer, "newer")}`
    }
    this._hideLoading()
  }

  // Decide which of two sides is older. Lower version number = older. When
  // versions are missing/equal, keep the given order (left treated as older).
  _orderByVersion(left, right) {
    const vl = parseInt(left && left.version, 10)
    const vr = parseInt(right && right.version, 10)
    if (!isNaN(vl) && !isNaN(vr) && vl > vr) return { older: right, newer: left }
    return { older: left, newer: right }
  }

  // Build a pane label that leads with the version (V1/V2) and notes which
  // side is older vs newer, e.g. "V1 — C-8 (older)"; with the set's tag when
  // there is one, "V2 · ADD-1 — C-8 (older)" (K7-21).
  _paneName(side, which) {
    const tag = side?.tag?.label ? ` · ${side.tag.label}` : ""
    const v = side && side.version ? `V${side.version}${tag} — ` : ""
    const tail = which ? ` (${which})` : ""
    return `${v}${(side && side.label) || ""}${tail}`
  }

  // The window title: the same words, with the tag as its colored chip.
  _paneTitleHtml(side, which) {
    const v = side && side.version ? `V${this._escapeHtml(String(side.version))} ${issueTagChip(side.tag)} — ` : ""
    const tail = which ? ` (${which})` : ""
    return `${v}${this._escapeHtml((side && side.label) || "")}${tail}`
  }

  // ── Text diff fallback ─────────────────────────────────────────
  _renderTextDiff(json) {
    const diff = Array.isArray(json.diff_payload) ? json.diff_payload : []
    const html = diff.map(([op, _pos, line]) => {
      const cls =
        op === "+" ? "bg-emerald-50 text-emerald-900" :
        op === "-" ? "bg-rose-50 text-rose-900 line-through" :
        "text-gray-700"
      return `<div class="px-2 py-0.5 ${cls} font-mono text-[12px] whitespace-pre-wrap">${this._escapeHtml(line || "")}</div>`
    }).join("")

    this._disposeInstance()
    if (this.hasViewerContainerTarget) {
      this.viewerContainerTarget.innerHTML =
        `<div class="overflow-y-auto max-h-full">${html}</div>`
    }
    if (this.hasTitleTarget) {
      this.titleTarget.textContent =
        `${json.left.label} ↔ ${json.right.label} (text diff)`
    }
    this._hideLoading()
  }

  _renderMetadataOnly(json) {
    const left = json.left || {}
    const right = json.right || {}
    const html = `
      <div class="p-6 text-center text-gray-700 space-y-3">
        <div class="text-[14px] font-medium">Side-by-side compare not available for this file type.</div>
        <div class="text-[12px] text-gray-500">
          ${this._escapeHtml(left.original_filename || left.label)}
          (${this._humanSize(left.byte_size)})
          → ${this._escapeHtml(right.original_filename || right.label)}
          (${this._humanSize(right.byte_size)})
        </div>
        <div class="text-[12px] text-gray-500">
          Download both versions to compare in your tool of choice.
        </div>
      </div>
    `
    this._disposeInstance()
    if (this.hasViewerContainerTarget) {
      this.viewerContainerTarget.innerHTML = html
    }
    this._hideLoading()
  }

  // ── Plumbing ────────────────────────────────────────────────────
  _revealRoot() {
    // The compare drawer is rendered as a sibling of the audit drawer
    // (root element controls the modal backdrop). Remove the `hidden`
    // class so it pops over the page.
    if (this.element && this.element.classList.contains("hidden")) {
      this.element.classList.remove("hidden")
    }
  }

  _showLoading() {
    if (this.hasLoadingTarget) this.loadingTarget.classList.remove("hidden")
  }

  _hideLoading() {
    if (this.hasLoadingTarget) this.loadingTarget.classList.add("hidden")
  }

  // A failed compare can be tried again without reloading the page (K7-20).
  // The message replaces the container's contents — Apryse's iframe with
  // them — so the old instance is dropped and the retry boots a fresh one.
  _showError(message) {
    this._hideLoading()
    this._disposeInstance()
    if (this.hasViewerContainerTarget) {
      this.viewerContainerTarget.innerHTML =
        `<div class="p-6 flex flex-col items-center gap-3 text-center">
          <div class="text-rose-700 text-[13px]">${this._escapeHtml(message)}</div>
          <button type="button" data-action="pdf-compare#retry"
                  class="px-3 py-1.5 text-sm font-medium text-white bg-brand rounded-lg hover:opacity-90 cursor-pointer">${this._escapeHtml(this.retryLabelValue)}</button>
        </div>`
    }
  }

  retry() {
    if (this._lastDetail) this._onOpen({ detail: this._lastDetail })
  }

  _disposeInstance() {
    if (!this._instance) return
    try { this._instance.UI.dispose() } catch (_) { /* never fully booted */ }
    this._instance = null
  }

  _csrfToken() {
    const el = document.querySelector('meta[name="csrf-token"]')
    return el ? el.content : ""
  }

  _humanSize(bytes) {
    if (!bytes) return "—"
    const units = ["B", "KB", "MB", "GB"]
    let size = bytes, i = 0
    while (size >= 1024 && i < units.length - 1) { size /= 1024; i++ }
    return `${size.toFixed(1)} ${units[i]}`
  }

  _escapeHtml(s) {
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
  }
}
